import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection, AdminStatCard } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

export default function AdminDashboard(props) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [activityPolicyState, setActivityPolicyState] = useState({
    loading: true,
    error: '',
    policy: { retention_days: 30 },
    feedback: '',
  });
  const [retentionDays, setRetentionDays] = useState('30');
  const [savingRetention, setSavingRetention] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      try {
        const response = await axiosInstance.get('/admin/dashboard');
        if (!active) {
          return;
        }
        setState({ loading: false, error: '', data: response.data });
      } catch (error) {
        if (!active) {
          return;
        }
        setState({
          loading: false,
          error: error?.response?.data?.message || 'Unable to load AMX admin dashboard data.',
          data: null,
        });
      }
    }

    loadDashboard();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadActivityPolicy() {
      try {
        const response = await axiosInstance.get('/admin/activity-log-policy');
        if (!active) {
          return;
        }
        const policy = response.data?.policy || { retention_days: 30 };
        setActivityPolicyState({ loading: false, error: '', policy, feedback: '' });
        setRetentionDays(String(policy.retention_days || 30));
      } catch (error) {
        if (!active) {
          return;
        }
        setActivityPolicyState({
          loading: false,
          error: error?.response?.data?.message || 'Unable to load activity log retention.',
          policy: { retention_days: 30 },
          feedback: '',
        });
      }
    }

    loadActivityPolicy();
    return () => {
      active = false;
    };
  }, []);

  async function saveRetention(event) {
    event.preventDefault();
    setSavingRetention(true);
    setActivityPolicyState((current) => ({ ...current, error: '', feedback: '' }));
    try {
      const response = await axiosInstance.patch('/admin/activity-log-policy', {
        retention_days: Number.parseInt(retentionDays, 10),
      });
      const policy = response.data?.policy || { retention_days: 30 };
      setActivityPolicyState({ loading: false, error: '', policy, feedback: 'Activity log retention updated.' });
      setRetentionDays(String(policy.retention_days || 30));
    } catch (error) {
      setActivityPolicyState((current) => ({
        ...current,
        error: error?.response?.data?.message || 'Unable to update activity log retention.',
      }));
    } finally {
      setSavingRetention(false);
    }
  }

  const summary = state.data?.summary || {};
  const cards = [
    { label: 'Active Users', value: summary.active_users || 0 },
    { label: 'Active Partners', value: summary.active_partners || 0 },
    {
      label: 'Gateways',
      value: summary.gateways?.total || 0,
      detail: `Active ${summary.gateways?.active || 0} | Inactive ${summary.gateways?.inactive || 0}`,
    },
    { label: 'MQTT Topics', value: summary.mqtt_topics || 0 },
    {
      label: 'Capabilities & Assets',
      value: summary.global_advertised_capabilities_assets || 0,
    },
    { label: 'Need Categories', value: summary.need_categories || 0 },
    {
      label: 'Shared Schemas',
      value: summary.shared_schemas || 0,
      detail: `${summary.shared_schema_versions || 0} schema versions registered`,
    },
    { label: 'Workspaces', value: summary.workspaces || 0 },
    { label: 'Messages', value: summary.messages || 0 },
  ];

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard' }]}
      title="AMX Admin Dashboard"
      subtitle="System-wide health, partner activity, and quick access to the admin registries."
    >
      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading admin summary...</Typography>
        </Stack>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {cards.map((card) => (
              <Grid key={card.label} size={{ xs: 12, sm: 6, lg: 4 }}>
                <AdminStatCard {...card} />
              </Grid>
            ))}
          </Grid>

          {summary.shared_schema_registry_status === 'unavailable' ? (
            <Alert severity="warning">
              Shared schema registry is unavailable. Dashboard totals are still shown, but schema registry data may be incomplete.
            </Alert>
          ) : null}

          <AdminSection
            title="Registry Shortcuts"
            subtitle="Jump directly into the main admin control pages."
            action={
              <Button component={RouterLink} to="/admin/partners" variant="contained">
                Open Partner Registry
              </Button>
            }
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
              <Button component={RouterLink} to="/admin/mqtt-topics" variant="outlined">MQTT Topics</Button>
              <Button component={RouterLink} to="/admin/gateways" variant="outlined">Gateways</Button>
              <Button component={RouterLink} to="/admin/need-categories" variant="outlined">Need Categories</Button>
              <Button component={RouterLink} to="/admin/shared-schemas" variant="outlined">Shared Schemas</Button>
              <Button component={RouterLink} to="/admin/account-security" variant="outlined">Account Security</Button>
              <Button component={RouterLink} to="/admin/account-compliance" variant="outlined">Account Compliance</Button>
              <Button component={RouterLink} to="/admin/activity-logs" variant="outlined">Activity Logs</Button>
              <Button component={RouterLink} to="/admin/workspaces" variant="outlined">Workspaces & Messages</Button>
              <Button component={RouterLink} to="/admin/demo-workspace" variant="outlined">Demo Workspace</Button>
            </Stack>
          </AdminSection>

          <AdminSection
            title="Activity Log Retention"
            subtitle="Controls when activity events become eligible for cleanup."
            action={
              <Button component={RouterLink} to="/admin/activity-logs" variant="outlined">
                Open Logs
              </Button>
            }
          >
            {activityPolicyState.feedback ? <Alert severity="success">{activityPolicyState.feedback}</Alert> : null}
            {activityPolicyState.error ? <Alert severity="error">{activityPolicyState.error}</Alert> : null}
            <Stack component="form" direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} onSubmit={saveRetention}>
              <TextField
                label="Retention Days"
                type="number"
                value={retentionDays}
                onChange={(event) => setRetentionDays(event.target.value)}
                inputProps={{ min: 1 }}
                size="small"
                required
                disabled={activityPolicyState.loading}
              />
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Current default is {activityPolicyState.policy?.retention_days || 30} days.
              </Typography>
              <Button type="submit" variant="contained" disabled={savingRetention || activityPolicyState.loading}>
                {savingRetention ? 'Saving...' : 'Save Retention'}
              </Button>
            </Stack>
          </AdminSection>

          <AdminSection
            title="Top Partner Activity"
            subtitle="Recent workspace and message activity grouped by partner."
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Workspaces</TableCell>
                  <TableCell align="right">Messages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(state.data?.top_partner_activity || []).map((partner) => (
                  <TableRow key={partner.partner_id} hover>
                    <TableCell>{partner.display_name}</TableCell>
                    <TableCell align="right">{partner.workspace_count}</TableCell>
                    <TableCell align="right">{partner.message_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AdminSection>
        </>
      )}
    </AdminLayout>
  );
}
